import { TranslateService } from '@ngx-translate/core';
import * as uuid from 'uuid';
import { DropdownModel } from './dropdown.model';
import { CompositeModel } from './composite.model';
import { TextModel } from '../primitive/text.model';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { BooleanModel } from '../primitive/boolean.model';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { ConfigConstants } from '../../config/config-constants';
import { ODataObject } from '../../interfaces/odata-model.interface';

const odataCreditFlag = 'CreditFlag';
const odataTitle = 'Title';

export class AttendeeModel extends CompositeModel {
    public id: string;
    public title: DropdownModel<TextModel>;
    public textTitle: TextModel;
    public firstName: TextModel;
    public lastName: TextModel;
    public mail: TextModel;
    public notReceivingCredit: DisplayDecorator<BooleanModel>;
    public signFlag: TextModel;
    public formSubmitted: string;

    private translate: TranslateService;
    public constructor(
        title = ConfigConstants.attendeeTitleTechnologist,
        textTitle = '',
        firstName = '',
        lastName = '',
        mail = '',
        notReceivingCredit = false,
        signFlag = 'N',
        id = '',
        formSubmitted = '',
    ) {
        super('AttendeeModel');
        const injector = CustomInjector.getInjector();
        const valueHelperService = injector.get(ValueHelperService);
        this.translate = injector.get(TranslateService);
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);

        this.id = id.length > 0 ? id : uuid.v4();
        const attendeeTitles = valueHelperService.getAttendeeTitles();
        const attendeeTitlesEntries = attendeeTitles.map(
            (vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel('Title', vh.descText)),
        );

        this.title = <DropdownModel<TextModel>>(
            this.set(new DropdownModel<TextModel>(odataTitle, attendeeTitlesEntries))
        );

        this.title.selectByValue(title);
        this.textTitle = new TextModel('TextTitle', textTitle);

        // if the user is choosing from the title dropdown the entry "Other"
        // then the user should have the possibility to enter the title as free text.
        // The "TextTitle" is only relevant for the Front-End.
        modelOptionsLoader.load(this.textTitle, 'ConfirmationAttendees', 'Title');

        this.firstName = <TextModel>this.set(new TextModel('Fname', firstName));
        modelOptionsLoader.load(this.firstName, 'ConfirmationAttendees', 'Firstname');
        this.lastName = <TextModel>this.set(new TextModel('Lname', lastName));
        modelOptionsLoader.load(this.lastName, 'ConfirmationAttendees', 'Lastname');
        this.mail = <TextModel>this.set(new TextModel('Email', mail));
        modelOptionsLoader.load(this.mail, 'ConfirmationAttendees', 'Mail');
        this.notReceivingCredit = <DisplayDecorator<BooleanModel>>(
            this.set(
                new DisplayDecorator(
                    <string>this.translate.instant('CONFIRMATION_ATTENDEELIST_NON_CREDIT_LABEL'),
                    new BooleanModel(odataCreditFlag, notReceivingCredit),
                ),
            )
        );
        this.signFlag = <TextModel>this.set(new TextModel('SignFlag', signFlag));

        this.formSubmitted = formSubmitted;
    }

    public getDisplayTitle(): string {
        const title =
            this.title.selectedEntry.displayName === ConfigConstants.attendeeTitleOther
                ? this.textTitle.value
                : this.title.selectedEntry.displayName;
        return title;
    }

    public getDisplayNotReceivingCredit(): string {
        return this.notReceivingCredit.value
            ? <string>this.translate.instant('CONFIRMATION_ATTENDEELIST_NON_CREDIT_LABEL_YES')
            : <string>this.translate.instant('CONFIRMATION_ATTENDEELIST_NON_CREDIT_LABEL_NO');
    }

    public getOutModel(): ODataAttendeeOutModel {
        const outModel = <ODataAttendeeOutModel>super.getOutModel();

        // PACE US expects for the CreditFlag either a 0 or a 1.
        outModel[odataCreditFlag] = outModel[odataCreditFlag] === ConfigConstants.sapTrue ? '1' : '0';

        // if the title has been set manually, we need ofc to set the title
        if (this.textTitle.value.length > 0) {
            outModel[odataTitle] = this.textTitle.value;
        }

        return outModel;
    }

    public toJSON(): unknown {
        return {
            id: this.id,
            title: this.title.selectedEntry ? this.title.selectedEntry.displayName : '',
            textTitle: this.textTitle.value,
            firstName: this.firstName.value,
            lastName: this.lastName.value,
            mail: this.mail.value,
            notReceivingCredit: this.notReceivingCredit.value,
            signFlag: this.signFlag.value,
            formSubmitted: this.formSubmitted,
        };
    }
}

export class AttendeeModelFromJson extends AttendeeModel {
    public constructor(data: JsonAttendeeModel) {
        super(
            data.title,
            data.textTitle,
            data.firstName,
            data.lastName,
            data.mail,
            data.notReceivingCredit,
            data.signFlag,
            data.id,
            data.formSubmitted,
        );

        if (this.title && data.title) {
            this.title.selectByDisplayName(data.title);
        }

        if (data.textTitle) {
            this.textTitle.value = data.textTitle;
        }
    }
}

export interface JsonAttendeeModel {
    id: string;
    title: string;
    textTitle: string;
    firstName: string;
    lastName: string;
    mail: string;
    notReceivingCredit: boolean;
    signFlag: string;
    formSubmitted: string;
}

export interface ODataAttendeeOutModel extends ODataObject {
    Title: string;
    Fname: string;
    Lname: string;
    Email: string;
    CreditFlag: string;
    SignFlag: string;
}
